import { Box, Dialog, Grid, makeStyles } from '@material-ui/core';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ApproveRecurringFileIcon } from '../../icons';
import {
  Container,
  Description,
  DescriptionError,
  Detail,
  DetailContainer,
  StyledButton,
  Title,
} from '../approve-enrollment-dialog/style';
import GcpTypography from '../gcp-typography';
import useViewModel from './viewmodel';

export interface FileTypeProps {
  file_name: string;
  id: string;
}

type ApproveRecurringFileDialogProps = {
  open: boolean;
  approvedLists: FileTypeProps[];
  failedLists: FileTypeProps[];
  allpass: boolean;
  showTab: boolean;
  onCancel?: () => void;
  onSubmit: () => void;
};

const useStyles = makeStyles((theme) => ({
  tabs: {
    boxShadow: '0px -1px 0px 0px #F0F0F0 inset',
  },
  tab: {
    minWidth: 'initial',
    '> .MuiTab-root': {
      textTransform: 'unset',
    },
  },
  selectedTab: {
    color: `${theme.palette.secondary.main} !important`,
    fontWeight: 500,
  },
  unselectedTab: {
    color: '#727272 !important',
    fontWeight: 400,
  },
}));

const ApproveRecurringFileDialog: FC<ApproveRecurringFileDialogProps> = (
  props
) => {
  const {
    open,
    approvedLists,
    failedLists,
    showTab,
    allpass,
    onCancel,
    onSubmit,
  } = props;

  const { t } = useTranslation();
  const classes = useStyles();
  const { value, fileLists, handleChangeTabs } = useViewModel({
    approvedLists,
    failedLists,
  });

  return (
    <Dialog open={open}>
      <Container>
        <ApproveRecurringFileIcon onlyApprove={allpass} />
        <div>
          <Title>{t('dialog.approve_recurring_file')}</Title>
        </div>
        {!showTab && (
          <Grid container>
            <Description>
              <span>
                {t(
                  approvedLists?.length > 0
                    ? 'dialog.total_approved_file'
                    : 'dialog.total_failed_to_approve_file',
                  { files: fileLists.length }
                )}
              </span>
            </Description>
          </Grid>
        )}
        {showTab && (
          <Box marginBottom={3}>
            <Tabs
              aria-label="secondary tabs example"
              indicatorColor="secondary"
              textColor="secondary"
              value={value}
              onChange={(_, newValue: string) => {
                handleChangeTabs(newValue);
              }}
            >
              <Tab
                label={
                  <GcpTypography
                    className={` ${
                      value === 'approved'
                        ? classes.selectedTab
                        : classes.unselectedTab
                    }`}
                  >
                    Approved ({approvedLists?.length || 0})
                  </GcpTypography>
                }
                value="approved"
              />
              <Tab
                label={
                  <GcpTypography
                    className={` ${
                      value === 'failed'
                        ? classes.selectedTab
                        : classes.unselectedTab
                    }`}
                  >
                    Failed to approve ({failedLists?.length || 0})
                  </GcpTypography>
                }
                value="failed"
              />
            </Tabs>
          </Box>
        )}

        <DetailContainer>
          {fileLists.map((item) => (
            <Detail key={item.id} className="list">
              <span>{t('dialog.file')}</span>
              <span className="value">{item.file_name || '-'}</span>
            </Detail>
          ))}
        </DetailContainer>

        {failedLists?.length > 0 &&
          (approvedLists?.length === 0 || value === 'failed') && (
            <Grid container>
              <DescriptionError>
                <span>
                  The file&apos;s processing dates are overdue. Once confirmed,
                  the status will update to &apos;Fail to approve&apos;.
                </span>
              </DescriptionError>
            </Grid>
          )}
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <StyledButton size="small" variant="outlined" onClick={onCancel}>
              {t('dialog.cancel')}
            </StyledButton>
          </Grid>
          <Grid item xs={6}>
            <StyledButton
              color="primary"
              size="small"
              variant="contained"
              onClick={onSubmit}
            >
              {t('dialog.confirm')}
            </StyledButton>
          </Grid>
        </Grid>
      </Container>
    </Dialog>
  );
};

export default ApproveRecurringFileDialog;
